import { defineComponent as _defineComponent } from 'vue'
import { computed, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router/composables';
import { useAlert, useGtag, useStore, useTranslate, useI18n } from '@/composables/use-globals';
import { EventBus } from '@/services/event-bus';
import Header from '@/components/common/Header.vue';
import Navigation from '@/components/common/Navigation.vue';
import GlobalAlert from '@/components/common/GlobalAlert.vue';
import CookieConsentBanner from '@/components/common/CookieConsentBanner.vue';
import NewVersionDialog from '@/components/common/NewVersionDialog.vue';
import { overrideDefaultTranslations } from '@/services/translation';
import { sendGALoginEvent } from '@/services/utils';
import { applyClientStylesheet } from '@/services/theme';
import { isProtectedPath } from './services/auth';
import { useMemberFiltersStore } from '@/stores/member-filters';
import { useMemberTagsStore } from '@/stores/member-tags';
import { useMaintenanceMode } from '@/composables/use-online';
import MaintenanceMode from '@/components/common/MaintenanceMode.vue';


export default /*#__PURE__*/_defineComponent({
  __name: 'Club',
  setup(__props) {

const drawer = ref(false);
const loading = ref(true);
const isAutomatedBrowser = navigator.webdriver;

const alert = useAlert();
const gtag = useGtag();
const i18n = useI18n();
const route = useRoute();
const router = useRouter();
const store = useStore();
const translate = useTranslate();
const memberFilterStore = useMemberFiltersStore();
const memberTagsStore = useMemberTagsStore();
const maintenanceMode = useMaintenanceMode();

onMounted(async () => {
  createEventBusListeners();
  await initializeStore();
  await initializeTranslations();
});

async function createEventBusListeners() {
  EventBus.$on('update-token', () => {
    store.commit('auth/retreiveAndUpdateToken');
  });
  EventBus.$on('user-login', async () => {
    await initializeStore();
    if (gtag) {
      sendGALoginEvent(
        gtag,
        store.getters['auth/loggedInUser'],
        'Club Portal Login',
        process.env.VUE_APP_CLUB_GOOGLE_ANALYTICS_ID
      );
    }
  });
  EventBus.$on('translations-initialized', async () => {
    document.title = translate('login.club_portal') as string;
  });
  EventBus.$on('invalid-token', async () => {
    await store.dispatch('auth/logout');
    if (isProtectedPath(route.path)) {
      router.push({ name: 'login' }).catch(() => {
        // ignore routing errors
      });
    }
  });
}

async function initializeStore() {
  loading.value = true;
  const loggedIn = await store.dispatch('auth/initialize');
  const client = store.getters['auth/clientDetails'];
  applyClientStylesheet(client?.theme_url);
  if (!client) {
    alert('error', 'Invalid hostname, please contact support');
  }
  const promises = [];
  if (loggedIn) {
    // default status filter to 'active' for organisations
    if (store.getters['auth/isOrganisationPortal']) {
      memberFilterStore.filters.default.status = ['active'];
      memberFilterStore.resetFilters();
      await memberFilterStore.applyFilters(store);
    }

    // Fetched here are multiple unrelated child components require this data.
    promises.push(store.dispatch('groups/fetchGroups'));
    if (store.getters['auth/isOrganisationPortal']) {
      promises.push(memberTagsStore.fetchMemberTags());
    } else {
      // Get scheme and all levels
      promises.push(store.dispatch('schemes/loadScheme', { raw: false, slug: 'default' }));
    }
    // Fetch clubs for login club switcher and languages
    promises.push(store.dispatch('clubs/fetchClubs'));
  }
  await Promise.all(promises);
  EventBus.$emit('user-initialized');
  loading.value = false;
}

/**
 * Merge the two translation files ready for use
 * Main file hosted in locales directory
 * Override file hosted on VUE_APP_TRANSLATION_URL
 * will only contain changes to the original en.json file
 */
async function initializeTranslations() {
  loading.value = true;
  const client = store.getters['auth/clientDetails'];
  const translationUrl = client ? client.translation_url : '';
  const localeMessages = await overrideDefaultTranslations(i18n.locale, i18n.messages, translationUrl);
  i18n.setLocaleMessage(i18n.locale, localeMessages);
  EventBus.$emit('translations-initialized');
  loading.value = false;
}

/**
 * Adjusts style by adding classes, based on possible external theme and possible content or route related changes
 */
const themeStyle = computed(() => {
  return store.getters['nav/themeStyle'];
});

const showPage = computed(() => {
  return !isProtectedPath(route.path) || !loading.value;
});

return { __sfc: true,drawer, loading, isAutomatedBrowser, alert, gtag, i18n, route, router, store, translate, memberFilterStore, memberTagsStore, maintenanceMode, createEventBusListeners, initializeStore, initializeTranslations, themeStyle, showPage, Header, Navigation, GlobalAlert, CookieConsentBanner, NewVersionDialog, MaintenanceMode }
}

})